import React, { ComponentClass, FunctionComponent, ReactElement, ReactNode } from "react";

import { DomHelper } from "./dom";

export class ReactHelper {
  /** Checks whether provided object is React class component. */
  public static isClassComponent(object: any | null | undefined): object is ComponentClass<any> {
    return !!object && typeof object === "function" && !!(object as any).prototype.isReactComponent;
  }

  /** Checks whether provided object is React function component. */
  public static isFunctionComponent(
    object: any | null | undefined,
  ): object is FunctionComponent<any> {
    return (
      !!object &&
      typeof object === "function" &&
      String(object).includes("return React.createElement")
    );
  }

  /** A wrapper for working with React.cloneElement with `ref` support */
  public static cloneWithRef<T>(
    element: React.ReactElement<any>,
    props: Partial<T & { ref?: React.Ref<any> }>,
  ) {
    return React.cloneElement(element as React.ReactElement<any>, props);
  }

  /** Checks whether provided object is React component. */
  public static isComponent(
    object: any | null | undefined,
  ): object is ComponentClass<any> | FunctionComponent<any> {
    return this.isClassComponent(object) || this.isFunctionComponent(object);
  }

  /** Checks whether provided object is React element (either a component or a DOM element). */
  public static isElement<P>(object: any | null | undefined): object is ReactElement<P> {
    return React.isValidElement<P>(object);
  }

  /** Creates new string with replaced value at index. */
  public static isEmptyNode(node: ReactNode | null | undefined) {
    throw new Error("Not implemented");
  }

  /** Ensures target element is DOM descendant of current element or stops propagation.
   * Hack to fix issue when parent A -> element B is clicked, and B is descendant of A in React component tree
   * but B is not descendant of A in DOM element tree, but click event is still propagated to the parent A.
   * E.g., when using React portals, B is rendered at document root (not descendant of A in DOM tree), but it's descendant in React tree. */
  public static ensureDomDescendantClickedOrStopPropagationAndPreventDefault(
    clickE: React.MouseEvent,
  ) {
    const currentEl = clickE.currentTarget as HTMLElement;
    const targetEl = clickE.target as HTMLElement;
    if (!(currentEl instanceof HTMLAnchorElement)) {
      return;
    }

    const isDescendantClicked = DomHelper.isDescendantClicked(clickE);
    let isDefaultPrevented = false;
    if (!isDescendantClicked) {
      clickE.stopPropagation();
      clickE.preventDefault();
      isDefaultPrevented = true;
    }

    // fix form nested in <a/> (nested in React tree) is not submitted when submit button clicked because click is prevented above:
    // manually submit form
    const submitButtonEl =
      targetEl instanceof HTMLButtonElement && targetEl.type === "submit" ? targetEl : undefined;
    const formEl = submitButtonEl ? targetEl.closest("form") : undefined;
    if (isDefaultPrevented && submitButtonEl && formEl) {
      if (typeof formEl.requestSubmit === "function") {
        formEl.requestSubmit(targetEl);
      } else {
        // fallback for older browsers
        const event = new Event("submit", { bubbles: true, cancelable: true });
        formEl.dispatchEvent(event);
      }
    }
  }
}
